import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useBlocker } from 'react-router-dom'

import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { popupFactory } from '@/factories'

export type UseNavigationGuardProps = {
  isBlocked: boolean
  message?: string
  title?: string
}

export const useNavigationGuard = ({ isBlocked, message, title }: UseNavigationGuardProps) => {
  const { t } = useTranslation(['common'])
  const blocker = useBlocker(isBlocked)

  const resolvedMessage = message ?? t('common:unsavedChanges.message')
  const resolvedTitle = title ?? t('common:unsavedChanges.title')

  useEffect(() => {
    if (blocker.state !== 'blocked') return

    popupFactory.open(ConfirmationPopup, {
      cancelText: t('common:unsavedChanges.stayButton'),
      children: resolvedMessage,
      confirmText: t('common:unsavedChanges.leaveButton'),
      onClose: () => {
        if (blocker.state === 'blocked') {
          blocker.reset()
        }
      },
      onConfirm: async () => {
        blocker.proceed()
      },
      title: resolvedTitle,
      variant: 'warning',
    })
  }, [resolvedTitle, resolvedMessage, blocker, t])

  useEffect(() => {
    if (!isBlocked) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isBlocked])
}
